import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0B0B0B] border-t border-[#FF8C00]/15 text-[#AAA29E] font-sans pt-16 pb-28 md:pb-16 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 glow-radial pointer-events-none opacity-20" />
      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Presentation */}
          <div className="flex flex-col gap-6">
            <Logo showText={true} className="h-12" />
            <p className="text-sm leading-relaxed mt-2 text-[#AAA29E]/80">
              ESHA — Eternal Soul Healing & Awakening is a comprehensive growth ecosystem integrating emotional intelligence, energy awareness, and life design to foster deep personal transformation.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com/eshawitheswari"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00D2FF] transition-colors text-xs font-mono tracking-widest uppercase font-bold"
                data-testid="footer-social-link-instagram"
              >
                Instagram ↗
              </a>
              <a
                href="https://facebook.com/eshawitheswari"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00D2FF] transition-colors text-xs font-mono tracking-widest uppercase font-bold"
                data-testid="footer-social-link-facebook"
              >
                Facebook ↗
              </a>
            </div>
          </div>

          {/* Navigation Directory */}
          <div className="flex flex-col gap-4">
            <h4 className="font-serif text-[#F6F4EE] text-lg font-medium tracking-wide border-b border-[#FF8C00]/10 pb-2">
              Explore ESHA
            </h4>
            <div className="flex flex-col gap-3 text-sm">
              <Link to="/" className="hover:text-[#00D2FF] transition-colors font-medium" data-testid="footer-link-home">
                Home Journey
              </Link>
              <Link to="/programs" className="hover:text-[#00D2FF] transition-colors font-medium" data-testid="footer-link-programs">
                Transformational Programs
              </Link>
              <Link to="/offerings" className="hover:text-[#00D2FF] transition-colors font-medium" data-testid="footer-link-offerings">
                Offerings Hub (Storefront)
              </Link>
              <Link to="/about" className="hover:text-[#00D2FF] transition-colors font-medium" data-testid="footer-link-about">
                About Founder & Vision
              </Link>
              <Link to="/contact" className="hover:text-[#00D2FF] transition-colors font-medium" data-testid="footer-link-contact">
                Contact & FAQs
              </Link>
            </div>
          </div>

          {/* Core Opportunities */}
          <div className="flex flex-col gap-4">
            <h4 className="font-serif text-[#F6F4EE] text-lg font-medium tracking-wide border-b border-[#FF8C00]/10 pb-2">
              Opportunities
            </h4>
            <div className="flex flex-col gap-3 text-sm">
              <a
                href="https://learn.eshawitheswari.com/l/96a7c7eae0"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00D2FF] transition-colors flex items-center gap-1.5 group font-medium"
                data-testid="footer-link-masterclass"
              >
                Emotional Freedom Masterclass
                <ArrowUpRight className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00D2FF] transition-colors flex items-center gap-1.5 group font-medium"
                data-testid="footer-link-clarity-call"
              >
                Book a Clarity Call
                <ArrowUpRight className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
              </a>
              <Link
                to="/offerings"
                className="hover:text-[#00D2FF] transition-colors flex items-center gap-1.5 group font-medium"
                data-testid="footer-link-calendar"
              >
                Saturday Workshops & Syllabi
                <ArrowUpRight className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>

          {/* Inspirational Manifesto Section */}
          <div className="flex flex-col gap-4">
            <h4 className="font-serif text-[#F6F4EE] text-lg font-medium tracking-wide border-b border-[#FF8C00]/10 pb-2">
              The Awakening
            </h4>
            <p className="text-sm italic leading-relaxed text-[#AAA29E]/95 font-serif">
              “From where you are now to the person you are capable of becoming. This is not about becoming perfect. It is about becoming conscious.”
            </p>
            <div className="mt-2 text-xs uppercase tracking-widest text-[#FF8C00] font-mono font-bold">
              — The ESHA Journey
            </div>
          </div>
        </div>

        {/* Dynamic Legal & Ethical Disclaimers */}
        <div className="border-t border-[#FF8C00]/10 pt-10 pb-6 flex flex-col gap-6 text-xs text-[#AAA29E]/70 leading-relaxed">
          <div className="flex items-start gap-2 bg-[#050505]/80 p-4 rounded-lg border border-[#FF8C00]/10 shadow-sm">
            <ShieldCheck className="h-5 w-5 text-[#FF8C00] shrink-0 mt-0.5" />
            <p>
              <strong>Professional Educational Disclaimer:</strong> ESHA — Eternal Soul Healing & Awakening, and its founder Eswari Podugu, provide coaching, education, emotional awareness resources, and energy-based personal growth systems. Our services, programs, workshops, and materials are intended entirely for self-improvement and self-awareness purposes. They do not constitute, and should not be used as a substitute for, clinical medical, psychiatric, or psychological diagnosis, advice, or treatment. ESHA does not treat, cure, or make medical claims regarding psychiatric or medical conditions. If you are experiencing clinical psychological or medical difficulties, please consult a licensed mental health professional or medical practitioner.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-[#FF8C00]/5 pt-6 text-center sm:text-left">
            <span>
              &copy; {currentYear} ESHA — Eternal Soul Healing & Awakening. All rights reserved. Hosted on the official ESHA learning platform.
            </span>
            <div className="flex gap-4 font-medium">
              <a href="#" className="hover:text-[#00D2FF] transition-colors" data-testid="footer-legal-link-privacy">Privacy Policy</a>
              <a href="#" className="hover:text-[#00D2FF] transition-colors" data-testid="footer-legal-link-terms">Terms of Service</a>
              <a href="#" className="hover:text-[#00D2FF] transition-colors" data-testid="footer-legal-link-disclaimer">Disclaimer</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
